import React, { useCallback } from 'react';
import { Slider, Typography, Tooltip, styled, SliderProps } from '@mui/material';
import { useIsIos } from '../../hooks/useIsIos';

// Custom styled components for the slider with simpler mobile-friendly design
export const StyledSlider = styled(Slider)(({ theme }) => ({
	height: 6,
	padding: '15px 0',
	'& .MuiSlider-track': {
		border: 'none',
		backgroundColor: theme.palette.primary.main,
		height: 6,
	},
	'& .MuiSlider-rail': {
		backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[300],
		opacity: 1,
		height: 6,
	},
	'& .MuiSlider-thumb': {
		height: 20,
		width: 20,
		backgroundColor: theme.palette.primary.main,
		boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
		'&:focus, &:hover': {
			boxShadow: `0 0 0 3px ${theme.palette.background.paper}`,
		},
		'&::before': {
			display: 'none',
		},
		'@media (max-width: 600px)': {
			height: 24,
			width: 24,
		},
	},
	'& .MuiSlider-valueLabel': {
		fontSize: 12,
		fontWeight: 'bold',
		top: -22,
		backgroundColor: theme.palette.primary.main,
		color: theme.palette.primary.contrastText,
		padding: '4px 8px',
		borderRadius: 4,
		'&:before': {
			display: 'none',
		},
	},
	'& .MuiSlider-mark': {
		backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[400],
		height: 6,
		width: 1,
		'&.MuiSlider-markActive': {
			backgroundColor: theme.palette.primary.main,
		},
	},
	'& .MuiSlider-markLabel': {
		fontSize: 11,
		'@media (max-width: 600px)': {
			// Only show labels for major marks on mobile
			display: (props: { 'data-value': number }) => (props['data-value'] % 5000 === 0 ? 'block' : 'none'),
		},
	},
}));

// Custom tooltip component for the slider thumb
interface ValueLabelProps {
	children: React.ReactElement;
	value: number;
}

export function ValueLabelComponent(props: Readonly<ValueLabelProps>) {
	const { children, value } = props;
	return (
		<Tooltip
			enterTouchDelay={0}
			placement='top'
			title={<Typography variant='body2'>{value.toLocaleString()} miles</Typography>}
		>
			{children}
		</Tooltip>
	);
}

// Create marks for the slider from 1000 to 30000 in steps of 5000
export const createMarks = () => {
	const marks = [];
	for (let miles = 0; miles <= 30000; miles += 5000) {
		marks.push({
			value: miles,
			label: `${miles / 1000}k`,
		});
	}

	return marks;
};

export interface MileageSliderProps extends Omit<SliderProps, 'onChange'> {
	value: number;
	onChange: (value: number) => void;
	minMileage?: number;
}

/**
 * A specialized slider component for selecting mileage values
 */
const MileageSlider: React.FC<MileageSliderProps> = ({ value, onChange, minMileage = 1000, ...sliderProps }) => {
	const isIos = useIsIos();

	const clampedOnChange = useCallback(
		(value: number) => {
			const mileage = Math.max(minMileage, value);
			onChange(mileage);
		},
		[onChange, minMileage],
	);

	return (
		<StyledSlider
			aria-label='Annual mileage'
			value={value}
			onChange={(e, newValue) => {
				if (isIos && e.type === 'mousedown') {
					//prevents MUI ios bug where small changes often jump back to original value https://github.com/mui/material-ui/issues/38311
					return;
				}
				clampedOnChange(Number(newValue));
			}}
			valueLabelDisplay='off'
			step={1000}
			marks={createMarks()}
			min={1000}
			max={30000}
			components={{
				ValueLabel: ValueLabelComponent,
			}}
			sx={{
				marginTop: 2,
				marginBottom: 2,
				mx: { sm: 0 }, // Add some side margin on mobile
				...sliderProps?.sx,
			}}
			{...sliderProps}
		/>
	);
};

export default MileageSlider;
